import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Mail, Lock, Save, Eye, EyeOff, ArrowLeft, 
  Shield, Calendar, Crown, Bell, BellOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Profile form
  const [profile, setProfile] = useState({
    name: '',
    email: ''
  });
  
  // Password form
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_orders: true,
    email_news: true,
    email_promotions: false
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || ''
      });
      setNotifications(user.notification_preferences || {
        email_orders: true,
        email_news: true,
        email_promotions: false
      });
    }
  }, [user, authLoading, navigate]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profiel bijgewerkt!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bijwerken mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }
    
    if (passwords.new.length < 8) {
      toast.error('Nieuw wachtwoord moet minimaal 8 tekens zijn');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Wachtwoord gewijzigd!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wachtwoord wijzigen mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/notifications`, notifications, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voorkeuren opgeslagen');
    } catch (error) {
      toast.error('Opslaan mislukt');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20 max-w-2xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Terug naar Dashboard
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Account</span>
          </div>
          <h1 className="text-4xl font-medium tracking-tight mb-3">
            Mijn <span className="brand-gold text-5xl">Profiel</span>
          </h1>
          <p className="text-gray-400">Beheer je accountinstellingen</p>
        </div>

        {/* Profile Info Section */}
        <div className="glass rounded-xl p-6 mb-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#D4AF37]" />
            Persoonlijke Gegevens
          </h2>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <Label htmlFor="name">Naam</Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Je naam"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="je@email.com"
                data-testid="profile-email-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="btn-gold"
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </form>
        </div>

        {/* Password Section */}
        <div className="glass rounded-xl p-6 mb-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            Wachtwoord Wijzigen
          </h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Huidig Wachtwoord</Label>
              <div className="relative mt-1">
                <Input
                  id="current-password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  className="bg-white/5 border-white/10 pr-10"
                  placeholder="••••••••"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="new-password">Nieuw Wachtwoord</Label>
              <div className="relative mt-1">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwords.new}
                  onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                  className="bg-white/5 border-white/10 pr-10"
                  placeholder="Minimaal 8 tekens"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-password">Bevestig Nieuw Wachtwoord</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading || !passwords.current || !passwords.new}
              className="btn-gold"
              data-testid="change-password-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              {loading ? 'Wijzigen...' : 'Wachtwoord Wijzigen'}
            </Button>
          </form>
        </div>

        {/* Notification Preferences */}
        <div className="glass rounded-xl p-6 mb-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#D4AF37]" />
            E-mail Voorkeuren
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Orderbevestigingen</p>
                <p className="text-sm text-gray-400">Ontvang bevestigingen van je aankopen</p>
              </div>
              <Switch
                checked={notifications.email_orders}
                onCheckedChange={(checked) => {
                  setNotifications({ ...notifications, email_orders: checked });
                  handleNotificationUpdate();
                }}
                data-testid="notification-orders"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Nieuwsupdates</p>
                <p className="text-sm text-gray-400">Blijf op de hoogte van nieuwe content</p>
              </div>
              <Switch
                checked={notifications.email_news}
                onCheckedChange={(checked) => {
                  setNotifications({ ...notifications, email_news: checked });
                  handleNotificationUpdate();
                }}
                data-testid="notification-news"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Promoties & Kortingen</p>
                <p className="text-sm text-gray-400">Ontvang speciale aanbiedingen</p>
              </div>
              <Switch
                checked={notifications.email_promotions}
                onCheckedChange={(checked) => {
                  setNotifications({ ...notifications, email_promotions: checked });
                  handleNotificationUpdate();
                }}
                data-testid="notification-promotions"
              />
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            Account Info
          </h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Lid sinds</span>
              <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString('nl-NL') : 'Onbekend'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Account type</span>
              <span className="flex items-center gap-1">
                {user?.is_admin ? (
                  <>
                    <Shield className="w-4 h-4 text-[#D4AF37]" />
                    Admin
                  </>
                ) : (
                  <>
                    <Crown className="w-4 h-4 text-[#D4AF37]" />
                    Member
                  </>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">E-mail geverifieerd</span>
              <span className="text-green-400">✓ Geverifieerd</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
